<template>
  <div class="w-full">
    <!-- Header Section -->
    <div class="mb-6">
      <!-- Job Status Notifications -->
      <div class="mb-4">
        <JobStatusNotification
          v-bind="jobNotifications.import"
          @retry="retryImportPbiData"
        />
        <JobStatusNotification
          v-bind="jobNotifications.plannerTemplate"
          @retry="retryPrepareTemplate"
        />
        <JobStatusNotification
          v-bind="jobNotifications.aiTemplate"
          @retry="retryPrepareAITemplate"
        />
      </div>
      
      <div class="flex justify-between items-center mb-2">
          <button
            v-if="canTriggerForecast"
            @click="openNewForecastModal"
            class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            New Forecast
          </button>
          
          <div class="relative group">
            <button
              v-if="canTriggerForecast"
              @click="importPbiData"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              :disabled="isImportLoading || isAutomaticImportInProgress"
              :class="{ 'opacity-50 cursor-not-allowed': isImportLoading || isAutomaticImportInProgress }"
            >
              <span v-if="isImportLoading" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Importing PBI Data...
              </span>
              <span v-else-if="isAutomaticImportInProgress" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Automatic Import in Progress...
              </span>
              <span v-else>
                <svg class="h-5 w-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                </svg>
                Import PBI Data
              </span>
            </button>
            
            <div v-if="isAutomaticImportInProgress" class="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              Automatic import is already in progress
            </div>
          </div>
      </div>
    </div>

    <!-- Reporting Periods Section -->
    <div class="mb-6">
      <div v-if="forecastingProcesses.length > 0" class="space-y-4">
        <div v-for="process in forecastingProcesses" :key="process.id" class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-bold">{{ process.name }} Reporting Period</h2>
          <p class="text-gray-600 mb-2">
            {{ formatDate(process.start_date) }} - {{ formatDate(process.end_date) }}
            <span v-if="getDaysRemaining(process.end_date) > 0">
              ({{ getDaysRemaining(process.end_date) }} days remaining)
            </span>
            <span v-else class="text-red-500">
              (Overdue)
            </span>
          </p>
          <div class="w-full bg-gray-200 rounded-full h-2.5">
            <div
              class="bg-blue-600 h-2.5 rounded-full"
              :style="{ width: getProgressPercentage(process.start_date, process.end_date) + '%' }"
            ></div>
          </div>
        </div>
      </div>
      <div v-else class="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
        <div class="flex flex-col items-center">
          <div class="mb-4 p-3 bg-gray-100 rounded-full">
            <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
      
          <!-- Automatic Processes Status -->
          <div v-if="activeAutomaticProcesses.length > 0" class="mt-4 p-4 bg-blue-50 rounded-lg">
            <h3 class="text-lg font-medium mb-2">Automatic Processes</h3>
            <div v-for="process in activeAutomaticProcesses" :key="process.id" class="mb-2">
              <div class="flex items-center">
                <div class="mr-2">
                  <svg v-if="process.status === JOB_STATUS.RUNNING || process.status === JOB_STATUS.CREATED" class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <svg v-else-if="process.status === JOB_STATUS.FINISHED" class="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <svg v-else-if="process.status === JOB_STATUS.FAILED" class="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <span>{{ process.type === JOB_TYPE.IMPORT_PBI_DATA ? 'Importing PBI Data' : 'Preparing Planner Template' }}: {{ formatJobStatus(process.status) }}</span>
              </div>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Active Reporting Periods</h3>
          <p class="text-gray-500 mb-6">Start a new forecast process to begin tracking reporting periods</p>
          <button
            @click="openNewForecastModal"
            v-if="canTriggerForecast"
            class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Start New Forecast
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Tasks Section -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div class="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-semibold">Tasks</h2>
              <p class="text-sm text-gray-500">Manage and track reporting tasks</p>
            </div>
            <button class="p-2 rounded-md hover:bg-gray-100">
              <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>

          <TaskView
            :myTasks="myTasks"
            :allTasks="openTasks"
            :reminders="reminders"
            :overdueRemindersCount="overdueRemindersCount"
            :canViewReminders="canViewReminders"
          />
        </div>
      </div>         <!-- Task Content -->

      <!-- Activity Feed Section -->
      <div class="lg:col-span-1">
        <ActivityFeed :activities="activities" />
      </div>
    </div>
  </div>

  <!-- New Forecast Modal -->
  <div v-if="showForecastModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
      <!-- Close button -->
      <button
        @click="showForecastModal = false"
        class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        <span class="text-xl">×</span>
      </button>
      
      <h2 class="text-xl font-bold mb-2">Create New Reporting Period</h2>
      <p class="text-gray-600 mb-6">Set up a new reporting period by providing a name and date range.</p>
      
      <div class="mb-4">
        <label for="periodName" class="block text-sm font-medium text-gray-700 mb-1">Period Name</label>
        <input
          type="text"
          id="periodName"
          v-model="periodName"
          placeholder="e.g. Q3 2025"
          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
      </div>
      
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="startDate" class="block text-sm text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              id="startDate"
              v-model="startDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
          <div>
            <label for="endDate" class="block text-sm text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              id="endDate"
              v-model="endDate"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
          </div>
        </div>
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showForecastModal = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="confirmCreateForecast"
          class="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Create Period</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Confirmation Dialog -->
  <div v-if="showConfirmDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
      <h3 class="text-lg font-medium mb-3">Confirm Forecast Creation</h3>
      <p class="text-gray-600 mb-6">Are you sure you want to create a new forecasting process? This action cannot be undone.</p>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showConfirmDialog = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          @click="startForecastProcess"
          class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Yes, Create</span>
        </button>
      </div>
    </div>
  </div>

  <!-- PBI Data Import Modal -->
  <div v-if="showPbiImportModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
      <!-- Close button -->
      <button
        @click="showPbiImportModal = false"
        class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        <span class="text-xl">×</span>
      </button>
      
      <h2 class="text-xl font-bold mb-2">Import PBI Data</h2>
      <p class="text-gray-600 mb-4">Enter the file paths for CSV files exported from Power BI.</p>
      <p class="text-gray-500 text-sm mb-6">
        Files should be accessible on the server. At least one file path is required.
        <br>Example: <code>/path/to/exported/data.csv</code>
      </p>
      
      <div class="mb-4">
        <label for="revExpFile" class="block text-sm font-medium text-gray-700 mb-1">Revenue/Expense File Path</label>
        <input
          type="text"
          id="revExpFile"
          v-model="revExpFilePath"
          placeholder="e.g. /path/to/revexp.csv"
          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
      </div>
      
      <div class="mb-6">
        <label for="bazaPLFile" class="block text-sm font-medium text-gray-700 mb-1">Baza PL File Path</label>
        <input
          type="text"
          id="bazaPLFile"
          v-model="bazaPLFilePath"
          placeholder="e.g. /path/to/bazapl.csv"
          class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="showPbiImportModal = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          :disabled="isImportLoading"
          :class="{ 'opacity-50': isImportLoading }"
        >
          Cancel
        </button>
        <button
          @click="confirmImportPbiData"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          :disabled="isImportLoading"
        >
          <span v-if="isImportLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
          <span v-else>Import Data</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, onUnmounted, reactive } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { BusinessRole } from '@/types/businessRole';
import { ProcessStatus } from '@/types/processStatus';
import { TaskDocProfile } from '@/features/task-management';
import TaskView from './tasks/TaskView.vue';
import ActivityFeed from './tasks/ActivityFeed.vue';
import JobStatusNotification from './JobStatusNotification.vue';
import { supabase } from '@/services/supabase';
import { TaskService } from '@/features/task-management';
import { DocumentService } from '@/features/document-management';
import { DOCUMENT_STATUS, REPORT_TYPE } from '@/types/document';
import { jobService } from '@/services/jobService';
import { JOB_TYPE, JOB_STATUS } from '@/types/job';
import { useDueDates } from '@/composables/useDueDates';
import { ACTIONS } from '@/types/taskActions';
import { logger, LogCategory } from '@/services/logger';
import axios from 'axios';

export default defineComponent({
  name: 'ForecastingPCODashboard',
  components: {
    TaskView,
    ActivityFeed,
    JobStatusNotification
  },
  setup() {
    // Auth
    const { user, profile, init } = useAuth();
    
    // Initialize services
    const taskService = new TaskService();
    const documentService = new DocumentService();
    
    // State for automatic import process
    const isAutomaticImportInProgress = ref(false);
    const importJobMonitoringIntervals = ref<Record<string, number>>({});
    const templateJobMonitoringIntervals = ref<Record<string, number>>({});
    const activeAutomaticProcesses = ref<Array<{
      id: string | undefined;
      type: JOB_TYPE;
      status: JOB_STATUS;
      forecastId: string;
      startTime: Date;
    }>>([]);
    
    // Define fixed polling intervals by job type (in milliseconds)
    const POLLING_INTERVALS = {
      [JOB_TYPE.IMPORT_PBI_DATA]: 60000,         // 60 seconds for import-pbi-data (longer running)
      [JOB_TYPE.PREPARE_PLANNER_TEMPLATE]: 15000, // 15 seconds for prepare-planner-template
      [JOB_TYPE.PREPARE_AI_TEMPLATE]: 15000       // 15 seconds for prepare-aigenerated-template
    };
    
    // Track polling intervals by job type
    const pollingIntervals = reactive<Record<JOB_TYPE, number | null>>({
      [JOB_TYPE.IMPORT_PBI_DATA]: null,
      [JOB_TYPE.PREPARE_PLANNER_TEMPLATE]: null,
      [JOB_TYPE.PREPARE_AI_TEMPLATE]: null
    });
    
    // Job notification states
    const jobNotifications = reactive({
      import: {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      },
      plannerTemplate: {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      },
      aiTemplate: {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      }
    });
    
    // Function to load tasks
    const loadTasks = async () => {
      try {
        console.log('Fetching tasks...');
        const data = await taskService.fetchTasks();
        console.log('Tasks data received:', data);
        allTasks.value = data || [];
        console.log('Tasks state updated:', allTasks.value);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    // Initialize auth state
    onMounted(async () => {
      await init();
      await loadTasks();
      
      // Add event listener for task refresh
      window.addEventListener('refresh-tasks', loadTasks);
    });
    
    // Check if user has Head of PCO role
    const canTriggerForecast = computed(() => {
      return true || profile.value?.business_role === BusinessRole.HEAD_OF_PCO;
    });

        // Computed property for all open tasks - tasks where done_at is null
    const openTasks = computed(() => {
      return allTasks.value.filter(task => task.done_at === null);
    });

    // Computed property to filter tasks for the current user
    const myTasks = computed(() => {
      const userId = user.value?.id;
      if (!userId) return [];

      return allTasks.value.filter(task =>
        task.assigned_user_id === userId && task.done_at === null
      );
    });

    // Computed property for reminders - tasks where done_at is null and action is 'DOWNLOAD'
    const reminders = computed(() => {
      return allTasks.value.filter(task =>
        task.done_at === null && task.action === 'DOWNLOAD'
      );
    });

    // Computed property for activities - tasks with done_at in the past
    const activities = computed(() => {
      const now = new Date();

      return allTasks.value.filter(task => {
        // Check if done_at exists and is not null
        if (!task.done_at) return false;

        // Compare the done_at date with current date
        const doneDate = new Date(task.done_at);
        return doneDate < now;
      });
    });

    // Computed property for overdue reminders count
    const overdueRemindersCount = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return reminders.value.filter(reminder => {
        if (!reminder.due_at) return false;
        const dueDate = new Date(reminder.due_at);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      }).length;
    });

    // Check if user has HEAD_OF_PCO or CONTROLLER role for viewing reminders
    const canViewReminders = computed(() => {
      return profile.value?.business_role === BusinessRole.HEAD_OF_PCO ||
             profile.value?.business_role === BusinessRole.CONTROLLER;
    });
    
    // Active tab state
    const activeTab = ref('my-tasks');
    
    // Reset activeTab if user doesn't have permission to view Reminders
    watch(canViewReminders, (hasPermission) => {
      if (!hasPermission && activeTab.value === 'reminders') {
        activeTab.value = 'my-tasks';
      }
    });

    // Store for all forecasting processes
    const forecastingProcesses = ref<any[]>([]);
    
    // Modal state
    const showForecastModal = ref(false);
    const showConfirmDialog = ref(false);
    
    // Process state
    const isLoading = ref(false);
    
    // Form data
    const periodName = ref('');
    const startDate = ref('');
    const endDate = ref('');
    
    // Simple notification function (can be replaced with a proper notification system later)
    const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
      console.log(`[${type.toUpperCase()}] ${message}`);
      // In a real implementation, this would show a UI notification
    };
    
    // Open the modal
    const openNewForecastModal = () => {
      // Reset form data
      periodName.value = '';
      startDate.value = '';
      endDate.value = '';
      showForecastModal.value = true;
    };
    
    // Show confirmation dialog
    const confirmCreateForecast = () => {
      showConfirmDialog.value = true;
    };
    
    // Function to start polling for status updates
    const statusPollingInterval = ref<number | null>(null);
    const processId = ref<string | null>(null);
    const processStatus = ref<string | ProcessStatus | null>(null);
    
    const startStatusPolling = (id: string) => {
      // Clear any existing interval
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
      
      // Set up polling every 5 seconds
      statusPollingInterval.value = window.setInterval(async () => {
        try {
          const { data, error } = await supabase
            .from('forecasting_process')
            .select('id, status')
            .eq('id', id)
            .single();
            
          if (error) throw error;
          
          processStatus.value = data.status;
          
          // If process is completed or errored, stop polling
          if (data.status === ProcessStatus.CREATED || data.status === ProcessStatus.COMPLETED) {
            if (statusPollingInterval.value) {
              clearInterval(statusPollingInterval.value);
            }
            
            // Show appropriate notification
            if (data.status === ProcessStatus.COMPLETED) {
              showNotification('Forecast process completed successfully');
            }
            
            // Refresh the list of forecasting processes
            fetchForecastingProcesses();
          }
        } catch (error) {
          console.error('Error polling for status:', error);
          if (statusPollingInterval.value) {
            clearInterval(statusPollingInterval.value);
          }
        }
      }, 5000);
    };
    
    // Function to fetch all forecasting processes
    const fetchForecastingProcesses = async () => {
      try {
        const { data, error } = await supabase
          .from('forecasting_process')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        forecastingProcesses.value = data || [];
        
        // If there's an active process, update the status indicator
        const activeProcess = data?.find(p => p.status === ProcessStatus.CREATED);
        if (activeProcess) {
          processId.value = activeProcess.id;
          processStatus.value = activeProcess.status;
          
          // Start polling for status updates
          startStatusPolling(activeProcess.id);
        }
      } catch (error) {
        console.error('Error fetching forecasting processes:', error);
      }
    };
    
    // Helper function to format dates - unified to handle both string and Date inputs
    const formatDate = (dateInput: string | Date): string => {
      if (!dateInput) return '';
      
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };
    
    // Helper function to calculate days remaining
    const getDaysRemaining = (endDateString: string) => {
      if (!endDateString) return 0;
      
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    };
    
    // Helper function to calculate progress percentage
    const getProgressPercentage = (startDateString: string, endDateString: string) => {
      if (!startDateString || !endDateString) return 0;
      
      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);
      const today = new Date();
      
      // Reset time part for accurate day calculation
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const totalDuration = endDate.getTime() - startDate.getTime();
      const elapsedDuration = today.getTime() - startDate.getTime();
      
      if (totalDuration <= 0) return 100;
      
      let percentage = (elapsedDuration / totalDuration) * 100;
      
      // Ensure percentage is between 0 and 100
      percentage = Math.max(0, Math.min(100, percentage));
      
      return percentage;
    };
    
    // Interval for polling job status
    const jobStatusPollingInterval = ref<number | null>(null);
    
    // Function to check for active import jobs
    const checkActiveImportJobs = async () => {
      try {
        console.log('Checking for active import jobs...');
        
        // Get all jobs of type IMPORT_PBI_DATA
        const importJobs = await jobService.getJobsByType(JOB_TYPE.IMPORT_PBI_DATA);
        
        // Check if there are any active jobs (CREATED or RUNNING)
        const activeJobs = importJobs.filter(job =>
          job.status === JOB_STATUS.CREATED || job.status === JOB_STATUS.RUNNING
        );
        
        // Set loading state based on active jobs
        if (activeJobs.length > 0) {
          console.log('Found active import jobs:', activeJobs);
          isImportLoading.value = true;
          
          // Start polling for job status updates if not already polling
          startJobStatusPolling();
        } else {
          isImportLoading.value = false;
          
          // Stop polling if no active jobs
          stopJobStatusPolling();
        }
      } catch (error) {
        console.error('Error checking for active import jobs:', error);
        isImportLoading.value = false;
      }
    };
    
    // Start polling for job status updates
    const startJobStatusPolling = () => {
      // Clear any existing interval
      stopJobStatusPolling();
      
      // Set up polling every 5 seconds
      jobStatusPollingInterval.value = window.setInterval(async () => {
        await checkActiveImportJobs();
      }, 5000);
    };
    
    // Stop polling for job status updates
    const stopJobStatusPolling = () => {
      if (jobStatusPollingInterval.value) {
        clearInterval(jobStatusPollingInterval.value);
        jobStatusPollingInterval.value = null;
      }
    };
    
    // Current forecast ID from active process
    const currentForecastId = computed(() => {
      const activeForecast = forecastingProcesses.value.find(p => p.status === ProcessStatus.CREATED);
      return activeForecast ? activeForecast.id : null;
    });
    
    // Check if import is in progress for current forecast
    const isImportInProgressForCurrentForecast = ref(false);
    
    // Start job-specific polling
    const startJobPolling = (jobType: JOB_TYPE) => {
      // Clear any existing interval for this job type
      if (pollingIntervals[jobType]) {
        clearInterval(pollingIntervals[jobType]!);
      }
      
      // Initial check
      checkJobStatus(jobType);
      
      // Set up polling with fixed interval based on job type
      pollingIntervals[jobType] = window.setInterval(() => {
        checkJobStatus(jobType).then(isRunning => {
          // If job is no longer running, stop polling for this job type
          if (!isRunning) {
            console.log(`Job ${jobType} is no longer running, stopping polling`);
            if (pollingIntervals[jobType]) {
              clearInterval(pollingIntervals[jobType]!);
              pollingIntervals[jobType] = null;
            }
          }
        });
      }, POLLING_INTERVALS[jobType]);
      
      console.log(`Started polling for ${jobType} with interval of ${POLLING_INTERVALS[jobType]/1000} seconds`);
    };
    
    // Check status for a specific job type
    const checkJobStatus = async (jobType: JOB_TYPE): Promise<boolean> => {
      if (!currentForecastId.value) return false;
      
      try {
        // Check job running status
        let isRunning = false;
        
        if (jobType === JOB_TYPE.IMPORT_PBI_DATA) {
          isRunning = await jobService.isJobRunning(
            currentForecastId.value,
            jobType
          );
          
          // Update UI state
          isImportInProgressForCurrentForecast.value = isRunning;
          
          // Check for failures
          const anyJobFailed = await jobService.hasAnyJobFailed(
            currentForecastId.value,
            jobType
          );
          
          const latestJobFailed = await jobService.hasLatestJobFailed(
            currentForecastId.value,
            jobType
          );
          
          // Update notification state
          if (latestJobFailed) {
            // Latest job failed - show red error
            jobNotifications.import = {
              show: true,
              isError: true,
              message: 'Data import failed',
              details: 'The most recent data import attempt failed. Please try again.',
              showRetryButton: true
            };
          } else if (anyJobFailed) {
            // Get the latest job to show its status
            const latestJob = await jobService.getLatestJobByForecastIdAndType(
              currentForecastId.value,
              jobType
            );
            
            // Only show warning if latest attempt is still in progress (CREATED or RUNNING)
            if (latestJob && (latestJob.status === JOB_STATUS.CREATED || latestJob.status === JOB_STATUS.RUNNING)) {
              const latestStatus = formatJobStatus(latestJob.status);
              
              jobNotifications.import = {
                show: true,
                isError: false,
                message: 'Previous data import attempts failed',
                details: `There were issues with previous data imports for this forecast. Latest attempt status: ${latestStatus}`,
                showRetryButton: false
              };
            } else {
              // If latest job is finished or not running, don't show warning
              jobNotifications.import.show = false;
            }
          } else if (!isRunning) {
            // No failures and not running - hide notification
            jobNotifications.import.show = false;
          }
        } else if (jobType === JOB_TYPE.PREPARE_PLANNER_TEMPLATE) {
          isRunning = await jobService.isJobRunning(
            currentForecastId.value,
            jobType
          );
          
          // Similar notification logic for planner template
          const anyJobFailed = await jobService.hasAnyJobFailed(
            currentForecastId.value,
            jobType
          );
          
          const latestJobFailed = await jobService.hasLatestJobFailed(
            currentForecastId.value,
            jobType
          );
          
          if (latestJobFailed) {
            jobNotifications.plannerTemplate = {
              show: true,
              isError: true,
              message: 'Planner template preparation failed',
              details: 'The most recent template preparation attempt failed.',
              showRetryButton: true
            };
          } else if (anyJobFailed) {
            // Get the latest job to show its status
            const latestJob = await jobService.getLatestJobByForecastIdAndType(
              currentForecastId.value,
              jobType
            );
            
            // Only show warning if latest attempt is still in progress (CREATED or RUNNING)
            if (latestJob && (latestJob.status === JOB_STATUS.CREATED || latestJob.status === JOB_STATUS.RUNNING)) {
              const latestStatus = formatJobStatus(latestJob.status);
              
              jobNotifications.plannerTemplate = {
                show: true,
                isError: false,
                message: 'Previous template preparation attempts failed',
                details: `There were issues with previous template preparations for this forecast. Latest attempt status: ${latestStatus}`,
                showRetryButton: false
              };
            } else {
              // If latest job is finished or not running, don't show warning
              jobNotifications.plannerTemplate.show = false;
            }
          } else if (!isRunning) {
            jobNotifications.plannerTemplate.show = false;
          }
        } else if (jobType === JOB_TYPE.PREPARE_AI_TEMPLATE) {
          isRunning = await jobService.isJobRunning(
            currentForecastId.value,
            jobType
          );
          
          // Similar notification logic for AI template
          const anyJobFailed = await jobService.hasAnyJobFailed(
            currentForecastId.value,
            jobType
          );
          
          const latestJobFailed = await jobService.hasLatestJobFailed(
            currentForecastId.value,
            jobType
          );
          
          if (latestJobFailed) {
            jobNotifications.aiTemplate = {
              show: true,
              isError: true,
              message: 'AI template preparation failed',
              details: 'The most recent AI template preparation attempt failed.',
              showRetryButton: true
            };
          } else if (anyJobFailed) {
            // Get the latest job to show its status
            const latestJob = await jobService.getLatestJobByForecastIdAndType(
              currentForecastId.value,
              jobType
            );
            
            // Only show warning if latest attempt is still in progress (CREATED or RUNNING)
            if (latestJob && (latestJob.status === JOB_STATUS.CREATED || latestJob.status === JOB_STATUS.RUNNING)) {
              const latestStatus = formatJobStatus(latestJob.status);
              
              jobNotifications.aiTemplate = {
                show: true,
                isError: false,
                message: 'Previous AI template preparation attempts failed',
                details: `There were issues with previous AI template preparations for this forecast. Latest attempt status: ${latestStatus}`,
                showRetryButton: false
              };
            } else {
              // If latest job is finished or not running, don't show warning
              jobNotifications.aiTemplate.show = false;
            }
          } else if (!isRunning) {
            jobNotifications.aiTemplate.show = false;
          }
        }
        
        return isRunning;
      } catch (error) {
        console.error(`Error checking status for job type ${jobType}:`, error);
        return false;
      }
    };
    
    // Retry handlers
    const retryImportPbiData = async () => {
      if (!currentForecastId.value) return;
      
      // Clear notification
      jobNotifications.import = {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      };
      
      // Trigger import again
      await triggerAutomaticImportPbiData(currentForecastId.value);
    };
    
    const retryPrepareTemplate = async () => {
      if (!currentForecastId.value) return;
      
      // Clear notification
      jobNotifications.plannerTemplate = {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      };
      
      // Trigger prepare template again
      await triggerAutomaticPrepareTemplate(currentForecastId.value);
    };
    
    const retryPrepareAITemplate = async () => {
      if (!currentForecastId.value) return;
      
      // Clear notification
      jobNotifications.aiTemplate = {
        show: false,
        isError: false,
        message: '',
        details: '',
        showRetryButton: false
      };
      
      // Trigger AI template preparation again - need to get the report type
      const reportTypes = Object.values(REPORT_TYPE).filter(type => type !== REPORT_TYPE.CAL);
      if (reportTypes.length > 0) {
        await triggerAutomaticAIGeneratedTemplate(currentForecastId.value, reportTypes[0]);
      }
    };
    
    // Function to monitor import job status with timeout
    const startImportJobMonitoring = async (jobId: string | undefined, forecastId: string) => {
      if (!jobId) {
        console.error('Cannot monitor job with undefined ID');
        return;
      }
      // Track start time for timeout calculation
      const startTime = new Date();
      const timeoutMinutes = 90;
      
      // Create a monitoring interval
      const monitoringInterval = window.setInterval(async () => {
        try {
          // Check current elapsed time
          const currentTime = new Date();
          const elapsedMinutes = (currentTime.getTime() - startTime.getTime()) / (1000 * 60);
          
          // If exceeded timeout, mark as failed
          if (elapsedMinutes > timeoutMinutes) {
            console.warn(`Import job ${jobId} exceeded ${timeoutMinutes} minute timeout`);
            
            // Update job status to FAILED
            await jobService.updateJobStatus(jobId, JOB_STATUS.FAILED);
            
            // Update active processes
            const processIndex = activeAutomaticProcesses.value.findIndex(p => p.id === jobId);
            if (processIndex !== -1) {
              activeAutomaticProcesses.value[processIndex].status = JOB_STATUS.FAILED;
            }
            
            showNotification(`Data import timed out after ${timeoutMinutes} minutes`, 'error');
            clearInterval(monitoringInterval);
            
            // Reset automatic import status
            isAutomaticImportInProgress.value = false;
            return;
          }
          
          // Get current job status
          const job = await jobService.getJobById(jobId);
          
          if (!job) {
            console.error(`Job with ID ${jobId} not found`);
            clearInterval(monitoringInterval);
            
            // Reset automatic import status
            isAutomaticImportInProgress.value = false;
            return;
          }
          
          // Update active processes
          const processIndex = activeAutomaticProcesses.value.findIndex(p => p.id === jobId);
          if (processIndex !== -1) {
            activeAutomaticProcesses.value[processIndex].status = job.status;
          }
          
          console.log(`Import job ${jobId} status: ${job.status}`);
          
          // If job is finished or failed, stop monitoring
          if (job.status === JOB_STATUS.FINISHED) {
            console.log(`Import job ${jobId} completed successfully`);
            showNotification('Data import completed successfully', 'success');
            clearInterval(monitoringInterval);
            
            // Reset automatic import status
            isAutomaticImportInProgress.value = false;
            
            // Trigger prepare-planner-template function
            await triggerAutomaticPrepareTemplate(forecastId);
          } else if (job.status === JOB_STATUS.FAILED) {
            console.error(`Import job ${jobId} failed`);
            showNotification('Data import failed', 'error');
            clearInterval(monitoringInterval);
            
            // Reset automatic import status
            isAutomaticImportInProgress.value = false;
          }
        } catch (error) {
          console.error('Error monitoring import job status:', error);
        }
      }, 30000); // Check every 30 seconds
      // Store the interval ID for cleanup
      importJobMonitoringIntervals.value[jobId] = monitoringInterval;
    };
    
    // Function to trigger AI-generated template automatically
    const triggerAutomaticAIGeneratedTemplate = async (forecastId: string, reportType: string) => {
      try {
        logger.info('Starting automatic AI-generated template preparation', LogCategory.GENERAL, { forecastId, reportType });
        
        // Create a job entry
        const job = await jobService.createJob(
          JOB_TYPE.PREPARE_AI_TEMPLATE,
          forecastId
        );
        
        if (!job || !job.id) {
          console.error('Failed to create job for automatic AI-generated template');
          showNotification('Failed to create job for automatic AI template preparation', 'error');
          return;
        }
        
        const jobId = job.id;
        console.log('Created job for automatic AI-generated template:', jobId);
        
        // Add to active processes
        activeAutomaticProcesses.value.push({
          id: jobId,
          type: JOB_TYPE.PREPARE_AI_TEMPLATE,
          status: JOB_STATUS.CREATED,
          forecastId,
          startTime: new Date()
        });
        
        // Construct the URL for the Fission Function
        const fissionUrl = 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/prepare-aigenerated-template';
        
        // Make the POST request with the job ID and report type
        const response = await fetch(fissionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            forecastId,
            reportType,
            jobId: jobId
          })
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Automatic AI-generated template initiated:', result);
        
        // Start monitoring the template job status
        startTemplateJobMonitoring(jobId);
        
        return jobId;
      } catch (error) {
        console.error('Error triggering automatic AI-generated template:', error);
        showNotification('Failed to start automatic AI template preparation', 'error');
      }
    };
    
    // Function to trigger prepare planner template automatically
    const triggerAutomaticPrepareTemplate = async (forecastId: string) => {
      try {
        logger.info('Starting automatic planner template preparation', LogCategory.GENERAL, { forecastId });
        
        // Get all report types except CAL
        const reportTypes = Object.values(REPORT_TYPE).filter(type => type !== REPORT_TYPE.CAL);
        
        // Process each report type
        for (const reportType of reportTypes) {
          try {
            // Create a job entry for planner template
            const job = await jobService.createJob(
              JOB_TYPE.PREPARE_PLANNER_TEMPLATE,
              forecastId
            );
            
            if (!job || !job.id) {
              console.error(`Failed to create job for automatic prepare planner template for ${reportType}`);
              continue; // Skip to next report type
            }
            
            const jobId = job.id;
            console.log(`Created job for automatic prepare planner template for ${reportType}:`, jobId);
            
            // Add to active processes
            activeAutomaticProcesses.value.push({
              id: jobId,
              type: JOB_TYPE.PREPARE_PLANNER_TEMPLATE,
              status: JOB_STATUS.CREATED,
              forecastId,
              startTime: new Date()
            });
            
            // Construct the URL for the Fission Function
            const fissionUrl = 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/prepare-planner-template';
            
            // Make the POST request with the job ID and report type
            const response = await fetch(fissionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                forecastId,
                reportType,
                jobId: jobId
              })
            });
            
            if (!response.ok) {
              throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            
            const result = await response.json();
            console.log(`Automatic prepare planner template for ${reportType} initiated:`, result);
            
            // Start monitoring the template job status
            startTemplateJobMonitoring(jobId);
            
            // Trigger AI-generated template for this report type
            await triggerAutomaticAIGeneratedTemplate(forecastId, reportType);
          } catch (error) {
            console.error(`Error triggering automatic prepare planner template for ${reportType}:`, error);
            // Continue with next report type
          }
        }
        
        return true;
      } catch (error) {
        console.error('Error triggering automatic prepare planner template:', error);
        showNotification('Failed to start automatic template preparation', 'error');
        return false;
      }
    };
    
    // Function to monitor template job status
    const startTemplateJobMonitoring = async (jobId: string) => {
      // Create a monitoring interval
      const monitoringInterval = window.setInterval(async () => {
        try {
          // Get current job status
          const job = await jobService.getJobById(jobId);
          
          if (!job) {
            console.error(`Template job with ID ${jobId} not found`);
            clearInterval(monitoringInterval);
            return;
          }
          
          // Update active processes
          const processIndex = activeAutomaticProcesses.value.findIndex(p => p.id === jobId);
          if (processIndex !== -1) {
            activeAutomaticProcesses.value[processIndex].status = job.status;
          }
          
          console.log(`Template job ${jobId} status: ${job.status}`);
          
          // If job is finished or failed, stop monitoring
          if (job.status === JOB_STATUS.FINISHED) {
            console.log(`Template job ${jobId} completed successfully`);
            showNotification('Template preparation completed successfully', 'success');
            clearInterval(monitoringInterval);
          } else if (job.status === JOB_STATUS.FAILED) {
            console.error(`Template job ${jobId} failed`);
            showNotification('Template preparation failed', 'error');
            clearInterval(monitoringInterval);
          }
        } catch (error) {
          console.error('Error monitoring template job status:', error);
        }
      }, 30000); // Check every 30 seconds
      
      // Store the interval ID for cleanup
      templateJobMonitoringIntervals.value[jobId] = monitoringInterval;
    };
    
    
    // Function to check for active automatic import jobs
    const checkForActiveImportJobs = async () => {
      try {
        const importJobs = await jobService.getJobsByType(JOB_TYPE.IMPORT_PBI_DATA);
        
        // Check if there are any active jobs (CREATED or RUNNING)
        const activeJobs = importJobs.filter(job =>
          job.status === JOB_STATUS.CREATED || job.status === JOB_STATUS.RUNNING
        );
        
        if (activeJobs.length > 0) {
          console.log('Found active automatic import jobs:', activeJobs);
          
          // Set automatic import in progress
          isAutomaticImportInProgress.value = true;
          
          // Add to active processes
          activeJobs.forEach(job => {
            activeAutomaticProcesses.value.push({
              id: job.id,
              type: JOB_TYPE.IMPORT_PBI_DATA,
              status: job.status,
              forecastId: job.forecast_id || '',
              startTime: new Date(job.created_at || Date.now())
            });
            
            // Start monitoring the job status
            startImportJobMonitoring(job.id, job.forecast_id || '');
          });
        }
      } catch (error) {
        console.error('Error checking for active automatic import jobs:', error);
      }
    };
    
    // Fetch forecasting processes on component mount
    onMounted(async () => {
      fetchForecastingProcesses();
      await checkActiveImportJobs();
      await checkForActiveImportJobs();
      
      // Start job-specific polling for all job types if there's an active forecast
      if (currentForecastId.value) {
        // Check each job type and start polling if needed
        const jobTypes = [
          JOB_TYPE.IMPORT_PBI_DATA,
          JOB_TYPE.PREPARE_PLANNER_TEMPLATE,
          JOB_TYPE.PREPARE_AI_TEMPLATE
        ];
        
        for (const jobType of jobTypes) {
          const isRunning = await jobService.isJobRunning(
            currentForecastId.value,
            jobType
          );
          
          if (isRunning) {
            console.log(`Found running job of type ${jobType}, starting polling`);
            startJobPolling(jobType);
          } else {
            // Even if not running, check for failures to show notifications
            await checkJobStatus(jobType);
          }
        }
      }
    });
    
    // Helper function to format job status
    const formatJobStatus = (status: JOB_STATUS): string => {
      switch (status) {
        case JOB_STATUS.CREATED:
          return 'Created';
        case JOB_STATUS.RUNNING:
          return 'Running';
        case JOB_STATUS.FINISHED:
          return 'Finished';
        case JOB_STATUS.FAILED:
          return 'Failed';
        default:
          return status;
      }
    };
    
    // Clean up on component unmount
    onUnmounted(() => {
      if (statusPollingInterval.value) {
        clearInterval(statusPollingInterval.value);
      }
      
      // Clear job status polling interval
      stopJobStatusPolling();
      
      // Clear automatic import job monitoring intervals
      Object.values(importJobMonitoringIntervals.value).forEach(interval => {
        clearInterval(interval);
      });
      
      // Clear template job monitoring intervals
      Object.values(templateJobMonitoringIntervals.value).forEach(interval => {
        clearInterval(interval);
      });
      
      // Clear job-specific polling intervals
      Object.keys(pollingIntervals).forEach(jobType => {
        if (pollingIntervals[jobType as JOB_TYPE]) {
          clearInterval(pollingIntervals[jobType as JOB_TYPE]!);
          pollingIntervals[jobType as JOB_TYPE] = null;
        }
      });
      
      // Reset automatic import status
      isAutomaticImportInProgress.value = false;
      
      // Remove event listener
      window.removeEventListener('refresh-tasks', loadTasks);
    });
    
    /**
     * Create a document and associated task for a forecasting process
     * @param processId The ID of the forecasting process
     * @param processName The name of the forecasting process
     * @param userId The ID of the user to assign the document and task to
     * @returns The created document ID or null if there was an error
     */
    const createDocumentAndTask = async (processId: string, processName: string, userId: string) => {
      try {
        // 1. Create the document using DocumentService
        const documentData = await documentService.createDocument({
          name: `${processName} - Calendar`,
          status: DOCUMENT_STATUS.CREATING,
          report_type: REPORT_TYPE.CAL,
          created_by: userId,
          forecast_id: processId
        });
        
        // 2. Calculate due date using the getDueDate function
        const { getDueDate } = useDueDates();
        const dueDateString = await getDueDate(ACTIONS.UPLOAD, documentData.id);
        
        // 3. Create the task using TaskService
        const taskResult = await taskService.createTask({
          due_at: dueDateString,
          action: 'UPLOAD',
          assigned_user_id: userId,
          document_id: documentData.id
        });
        
        if (!taskResult) {
          console.error('Error creating task');
          throw new Error('Failed to create task');
        }
        
        return documentData.id;
      } catch (error) {
        console.error('Error in createDocumentAndTask:', error);
        return null;
      }
    };
    
    // Start the forecast process
    const startForecastProcess = async () => {
      // Set loading state
      isLoading.value = true;
      
      try {
        if (!user.value) {
          // showNotification('User not authenticated', 'error');
          // return;
        }

        // Format dates as ISO strings for timestamp
        const formattedStartDate = startDate.value ? new Date(startDate.value).toISOString() : null;
        const formattedEndDate = endDate.value ? new Date(endDate.value).toISOString() : null;
        
        // 1. Create the forecasting process
        const { data, error } = await supabase
          .from('forecasting_process')
          .insert({
            name: periodName.value,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            status: ProcessStatus.CREATED
          })
          .select();
          
        if (error) throw error;
        
        const newProcessId = data[0].id;
        
        // Process the response
        processId.value = newProcessId;
        processStatus.value = ProcessStatus.CREATED;
        
        // Close confirmation dialog but keep modal open
        showConfirmDialog.value = false;

        // Get current user ID for document creation
        const { data: userData } = await supabase.auth.getSession()
        const currentUserId = userData?.session?.user.id
        
        // Ensure we have a valid user ID
        if (!currentUserId) {
          console.error('No user ID available, cannot create documents');
          throw new Error('User ID not available');
        }
        
        // Create the calendar document and associated task
        if (currentUserId) {
          await createDocumentAndTask(newProcessId, periodName.value, currentUserId);
        } else {
          console.error('User ID not available, cannot create document and task');
        }
        
        showNotification('Forecast process started successfully');
        
        // Start polling for status updates
        startStatusPolling(newProcessId);
        
        // Automatically trigger import-pbi-data with the new forecast ID
        await triggerAutomaticImportPbiData(newProcessId);
        
        // Fetch updated data from backend
        await fetchForecastingProcesses();
        
        // Refresh tasks to include the newly created task
        await loadTasks();
        
        // Now close the modal
        showForecastModal.value = false;
      } catch (error) {
        console.error('Error starting forecast process:', error);
        processStatus.value = 'error';
        showNotification('Failed to start forecast process', 'error');
      } finally {
        // Reset loading state
        isLoading.value = false;
      }
    };
    
    // Function to trigger automatic import of PBI data
    const triggerAutomaticImportPbiData = async (forecastId: string) => {
      try {
        console.log('Starting automatic PBI data import for forecast:', forecastId);
        
        // Set automatic import in progress
        isAutomaticImportInProgress.value = true;
        
        // Reset notification state
        jobNotifications.import = {
          show: false,
          isError: false,
          message: '',
          details: '',
          showRetryButton: false
        };
        
        // Create a job entry
        const job = await jobService.createJob(
          JOB_TYPE.IMPORT_PBI_DATA,
          forecastId
        );
        
        if (!job || !job.id) {
          console.error('Failed to create job for automatic PBI data import');
          showNotification('Failed to create job for automatic data import', 'error');
          isAutomaticImportInProgress.value = false;
          
          // Update notification state
          jobNotifications.import = {
            show: true,
            isError: true,
            message: 'Failed to create import job',
            details: 'Could not create a job for PBI data import.',
            showRetryButton: true
          };
          
          return;
        }
        
        const jobId = job.id;
        console.log('Created job for automatic PBI data import:', jobId);
        
        // Add to active processes
        activeAutomaticProcesses.value.push({
          id: jobId,
          type: JOB_TYPE.IMPORT_PBI_DATA,
          status: JOB_STATUS.CREATED,
          forecastId,
          startTime: new Date()
        });
        
        // Start job-specific polling
        startJobPolling(JOB_TYPE.IMPORT_PBI_DATA);
        
        // Prepare request payload with job ID and file paths
        const payload = {
          'forecastId': forecastId,
          'jobId': jobId,
          'file-revexp': 'revexp_data.csv',
          'file-baza-pl': 'baza_PL-data_COM.csv'
        };
        
        // Make API call to the import-pbi-data function
        const fissionUrl = 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/import-pbi-data';
        const response = await axios.post(fissionUrl, payload);
        
        console.log('Automatic PBI data import initiated:', response.data);
        
        // Start monitoring the job status
        startImportJobMonitoring(jobId, forecastId);
        
        return jobId;
      } catch (error) {
        console.error('Error triggering automatic PBI data import:', error);
        showNotification('Failed to start automatic data import', 'error');
        
        // Reset automatic import status on error
        isAutomaticImportInProgress.value = false;
      }
    };
    
    // All tasks will be populated from the database
    const allTasks = ref<TaskDocProfile[]>([]);
    
    // PBI Data Import state
    const showPbiImportModal = ref(false);
    const revExpFilePath = ref('');
    const bazaPLFilePath = ref('');
    const isImportLoading = ref(false);
    
    // Open PBI Import modal
    const importPbiData = () => {
      revExpFilePath.value = '';
      bazaPLFilePath.value = '';
      showPbiImportModal.value = true;
    };
    
    // Import PBI data
    const confirmImportPbiData = async () => {
      let jobId: string | null = null;
      
      try {
        // Validate that at least one file path is provided
        if (!revExpFilePath.value && !bazaPLFilePath.value) {
          showNotification('Please provide at least one file path', 'error');
          return;
        }
        
        isImportLoading.value = true;
        
        // Create a job entry
        const job = await jobService.createJob(
          JOB_TYPE.IMPORT_PBI_DATA,
          processId.value || undefined
        );
        
        if (!job || !job.id) {
          console.error('Failed to create job for PBI data import');
          showNotification('Failed to create job for tracking', 'error');
          return;
        }
        
        jobId = job.id;
        console.log('Created job for PBI data import:', jobId);
        
        // Prepare request payload with job ID
        const payload = {
          'file-revexp': revExpFilePath.value || null,
          'file-baza-pl': bazaPLFilePath.value || null,
          'jobId': jobId
        };
        
        // Make API call to the import-pbi-data function
        // Using the production URL from other components
        const fissionUrl = 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/import-pbi-data';
        const response = await axios.post(fissionUrl, payload);
        
        console.log('PBI data import response:', response.data);
        
        // Update job status to FINISHED
        if (jobId) {
          await jobService.updateJobStatus(jobId, JOB_STATUS.FINISHED);
        }
        
        // Show success notification with record counts
        const revExpCount = response.data?.revExp?.recordCount || 0;
        const bazaPLCount = response.data?.bazaPL?.recordCount || 0;
        
        let successMessage = 'PBI data imported successfully';
        if (revExpCount > 0 || bazaPLCount > 0) {
          successMessage += `: ${revExpCount} RevExp records, ${bazaPLCount} BazaPL records`;
        }
        
        showNotification(successMessage);
        
        // Close the modal
        showPbiImportModal.value = false;
      } catch (error: any) {
        console.error('Error importing PBI data:', error);
        
        // Update job status to FAILED if we have a job ID
        if (jobId) {
          await jobService.updateJobStatus(jobId, JOB_STATUS.FAILED);
        }
        
        // Provide more detailed error information
        let errorMessage = 'Failed to import PBI data';
        
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
          
          errorMessage += `: ${error.response.status} ${error.response.statusText || ''}`;
        } else if (error.request) {
          // The request was made but no response was received
          console.error('Error request:', error.request);
          errorMessage += ': No response received from server';
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error message:', error.message || 'Unknown error');
          errorMessage += `: ${error.message || 'Unknown error'}`;
        }
        
        showNotification(errorMessage, 'error');
      } finally {
        isImportLoading.value = false;
      }
    };

    return {
      // Dashboard functionality
      canTriggerForecast,
      forecastingProcesses,
      getDaysRemaining,
      getProgressPercentage,
      openNewForecastModal,
      confirmCreateForecast,
      startForecastProcess,
      myTasks,
      allTasks,
      openTasks,
      reminders,
      activities,
      overdueRemindersCount,
      canViewReminders,
      isLoading,
      showForecastModal,
      showConfirmDialog,
      periodName,
      startDate,
      endDate,
      formatDate,
      
      // PBI Data Import functionality
      importPbiData,
      confirmImportPbiData,
      showPbiImportModal,
      revExpFilePath,
      bazaPLFilePath,
      isImportLoading,
      
      // Automatic import functionality
      isAutomaticImportInProgress,
      activeAutomaticProcesses,
      formatJobStatus,
      JOB_STATUS,
      JOB_TYPE,
      
      // Job monitoring and notifications
      jobNotifications,
      retryImportPbiData,
      retryPrepareTemplate,
      retryPrepareAITemplate,
      currentForecastId,
      isImportInProgressForCurrentForecast
    };
  }
});
</script>